import { LoadingTile } from '@carvana/merch-tile';
import { memo, useContext } from 'react';
import LiveAnnouncer from '@/common/LiveAnnouncer';
import ErrorBoundary from '@/srp/ErrorBoundary';
import TileRenderer from '@/srp/ResultsSection/TileRenderer';
import { LOW_RESULTS_THRESHOLD } from '~/constants/srp';
import InventoryContext, {
  useFreeTextNoResults,
  useHasInventoryError,
  useInventoryIsLoaded,
  useSearchRequestId
} from '~/context/srp/InventoryContext';
import SearchContext from '~/context/srp/SearchContext';
import FreeTextNoResultsSection from './FreeTextNoResultsSection';
import { usePagination, useTiles } from './hooks';
import LowResultsSection from './LowResultsSection';
import Pagination from './Pagination';
import StyledResultsSection from './StyledResultSection';
import ZeroResultsSection from './ZeroResultsSection';
import ErrorSection from '../ErrorSection';

export default memo(function ResultsSection() {
  const {
    similarVehicles: { vehicles: similarVehicles }
  } = useContext(InventoryContext);
  const { resultsSectionRef } = useContext(SearchContext);

  const freeTextNoResults = useFreeTextNoResults();
  const hasInventoryError = useHasInventoryError();
  const inventoryLoaded = useInventoryIsLoaded();
  const searchRequestId = useSearchRequestId();
  const {
    pagination: { totalMatchedPages = 0 }
  } = usePagination();
  const tiles = useTiles();

  const hasTiles = tiles.length > 0;
  const isLoaded =
    inventoryLoaded && (Boolean(searchRequestId) || hasInventoryError);
  const showLowResults =
    hasTiles &&
    totalMatchedPages === 1 &&
    similarVehicles.length > 0 &&
    tiles.length < LOW_RESULTS_THRESHOLD &&
    isLoaded;

  const showPagination = hasTiles && isLoaded && totalMatchedPages >= 2;

  if (freeTextNoResults) {
    return <FreeTextNoResultsSection />;
  }

  return (
    <>
      <LiveAnnouncer
        message={
          isLoaded && hasTiles
            ? `Showing ${tiles.length.toLocaleString()} results`
            : ''
        }
      />
      {isLoaded ? (
        <>
          {hasTiles ? (
            <StyledResultsSection id="results-section">
              <TileRenderer tiles={tiles} />
            </StyledResultsSection>
          ) : (
            <ErrorBoundary>
              {hasInventoryError ? <ErrorSection /> : <ZeroResultsSection />}

              <StyledResultsSection>
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} role="listitem" />
                ))}
              </StyledResultsSection>
            </ErrorBoundary>
          )}
        </>
      ) : (
        <StyledResultsSection
          id="results-section"
          data-testid="search-results-loading"
        >
          {Array.from({ length: 24 }).map((_, x) => (
            <div key={x} role="listitem">
              <LoadingTile />
            </div>
          ))}
        </StyledResultsSection>
      )}
      <div ref={resultsSectionRef} />
      {showLowResults && <LowResultsSection />}
      {showPagination && <Pagination />}
    </>
  );
});
